import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
} from '@mui/material';
import { CheckCircle, ErrorOutline, EventBusy } from '@mui/icons-material';
import {
  patientsService,
  PatientIntakeState,
  PatientIntakeFormData,
} from '../services/patientsService';
import PhoneWithCountryInput from '../components/PhoneWithCountryInput';
import { PhoneValue, phoneNational, phoneValueFromParts } from '../utils/phone';
import { formatCpf, isValidCpf, formatCep, fetchAddressByCep, ageFromBirthDate, isValidEmail, isFutureBirthDate, todayIso } from '../utils/documents';

// Página PÚBLICA de preenchimento de cadastro (RN-112f): aberta pelo link do email,
// sem sessão/menu — mobile-first (o responsável abre no celular). Só os campos
// cadastrais da 1ª aba do cadastro de paciente; nada de dados clínicos.

const NAME_MAX = 100;
const DOC_MAX = 14;
const EMAIL_MAX = 100;
const NOTES_MAX = 500;

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'white',
    '& fieldset': { borderColor: '#ced4da', legend: { maxWidth: '100%' } },
    '&:hover fieldset': { borderColor: '#ced4da' },
    '&.Mui-focused fieldset': {
      borderColor: '#03B4C6',
      boxShadow: '0 0 0 3px rgba(3, 180, 198, 0.1)',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.95rem',
    color: '#6c757d',
    backgroundColor: 'white',
    paddingLeft: '4px',
    paddingRight: '4px',
    '&.Mui-focused': { color: '#03B4C6' },
  },
};

type FormState = Record<string, string>;
type PhoneField = 'phone' | 'responsiblePhone1' | 'responsiblePhone2';
type PhoneState = Record<PhoneField, PhoneValue>;

// Os telefones vêm como número nacional + DDI à parte; o seletor de país edita os dois.
const toPhoneState = (p: PatientIntakeFormData | null): PhoneState => ({
  phone: phoneValueFromParts(p?.phoneDdi, null, p?.phone),
  responsiblePhone1: phoneValueFromParts(p?.responsiblePhoneDdi1, null, p?.responsiblePhone1),
  responsiblePhone2: phoneValueFromParts(p?.responsiblePhoneDdi2, null, p?.responsiblePhone2),
});

const toFormState = (p: PatientIntakeFormData | null): FormState => ({
  patientName: p?.patientName || '',
  birthDate: p?.birthDate ? p.birthDate.substring(0, 10) : '',
  genderId: p?.genderId || '',
  document: p?.document || '',
  email: p?.email || '',
  responsibleName1: p?.responsibleName1 || '',
  responsibleDocument1: p?.responsibleDocument1 || '',
  responsibleEmail1: p?.responsibleEmail1 || '',
  responsibleName2: p?.responsibleName2 || '',
  responsibleDocument2: p?.responsibleDocument2 || '',
  responsibleEmail2: p?.responsibleEmail2 || '',
  addressZipCode: p?.addressZipCode || '',
  addressStreet: p?.addressStreet || '',
  addressNumber: p?.addressNumber || '',
  addressComplement: p?.addressComplement || '',
  addressDistrict: p?.addressDistrict || '',
  addressCity: p?.addressCity || '',
  addressState: p?.addressState || '',
  patientNotes: p?.patientNotes || '',
  registerOrigin: p?.registerOrigin || '',
  referredBy: p?.referredBy || '',
});

// Mesmas opções da tela de cadastro (canal de entrada)
const ENTRY_CHANNELS = [
  'Instagram', 'Facebook', 'Google', 'Site',
  'Indicação profissional', 'Indicação de conhecido', 'Outros',
];

const PatientIntake: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const alias = params.get('clinic') || '';
  const token = params.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<PatientIntakeState | null>(null);
  const [form, setForm] = useState<FormState>(toFormState(null));
  const [phones, setPhones] = useState<PhoneState>(toPhoneState(null));
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  // Trava de segurança: confirmar a data de nascimento libera o formulário
  const [verified, setVerified] = useState(false);
  const [verifyDate, setVerifyDate] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState('');

  useEffect(() => {
    if (!alias || !token) {
      setState({ status: 'invalid', clinicName: null, prefill: null, genders: [], phoneCountries: [] });
      setLoading(false);
      return;
    }
    patientsService.getIntake(alias, token)
      .then(setState)
      .catch(() => setState({ status: 'invalid', clinicName: null, prefill: null, genders: [], phoneCountries: [] }))
      .finally(() => setLoading(false));
  }, [alias, token]);

  const [cepLoading, setCepLoading] = useState(false);

  const setField = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const setPhone = (field: PhoneField) => (value: PhoneValue) => {
    setPhones((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  // Menor de 18: contatos do responsavel obrigatorios; maior: contatos do paciente
  const age = ageFromBirthDate(form.birthDate);
  const isMinor = age !== null && age < 18;
  // A clínica precisa de UM canal de contato completo — do paciente ou do responsável.
  // Ter os dois é bem-vindo, mas exigir ambos só duplicaria o mesmo telefone/email.
  const patientContactOk = Boolean(phoneNational(phones.phone) && form.email.trim());
  const responsibleContactOk = Boolean(phoneNational(phones.responsiblePhone1) && form.responsibleEmail1.trim());
  const hasAnyContact = patientContactOk || responsibleContactOk;

  // CEP preenche o endereco automaticamente (ViaCEP)
  const handleCepBlur = async () => {
    if (form.addressZipCode.replace(/\D/g, '').length !== 8) return;
    setCepLoading(true);
    const address = await fetchAddressByCep(form.addressZipCode);
    setCepLoading(false);
    if (!address) {
      setError('CEP nao encontrado. Preencha o endereco manualmente.');
      return;
    }
    setForm((prev) => ({
      ...prev,
      addressStreet: address.street || prev.addressStreet,
      addressDistrict: address.district || prev.addressDistrict,
      addressCity: address.city || prev.addressCity,
      addressState: address.state || prev.addressState,
    }));
  };

  const handleVerify = async () => {
    if (!verifyDate) {
      setVerifyError('Informe a data de nascimento do paciente.');
      return;
    }
    setVerifying(true);
    setVerifyError('');
    try {
      const s = await patientsService.verifyIntake(alias, token, verifyDate);
      setState(s);
      setForm(toFormState(s.prefill));
      setPhones(toPhoneState(s.prefill));
      setVerified(true);
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === 'Patient.IntakeRevoked') {
        setState((prev) => prev ? { ...prev, status: 'revoked' } : prev);
      } else if (code === 'Patient.IntakeAlreadyCompleted') {
        setState((prev) => prev ? { ...prev, status: 'completed' } : prev);
      } else {
        setVerifyError(err?.response?.data?.description || 'Não foi possível verificar. Tente novamente.');
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.patientName.trim() || !form.birthDate) {
      setError('Nome completo e data de nascimento sao obrigatorios.');
      return;
    }
    if (isFutureBirthDate(form.birthDate)) {
      setError('A data de nascimento nao pode ser posterior a data de hoje.');
      return;
    }
    if (form.document.trim() && !isValidCpf(form.document)) {
      setError('CPF do paciente invalido. Confira os numeros informados.');
      return;
    }
    if (form.responsibleDocument1.trim() && !isValidCpf(form.responsibleDocument1)) {
      setError('CPF do responsavel invalido. Confira os numeros informados.');
      return;
    }
    const emails: [string, string][] = [
      [form.email, 'do paciente'],
      [form.responsibleEmail1, 'do responsavel 1'],
      [form.responsibleEmail2, 'do responsavel 2'],
    ];
    const invalidEmail = emails.find(([value]) => value.trim() && !isValidEmail(value));
    if (invalidEmail) {
      setError(`Email ${invalidEmail[1]} invalido. Confira o endereco informado.`);
      return;
    }
    if (!form.registerOrigin) {
      setError('Informe como conheceu a clinica.');
      return;
    }
    if (form.registerOrigin.includes('Indicação') && !form.referredBy.trim()) {
      setError('Informe quem indicou a clinica.');
      return;
    }
    if (isMinor && !form.responsibleName1.trim()) {
      setError('Para paciente menor de 18 anos, o nome do responsavel e obrigatorio.');
      return;
    }
    if (!hasAnyContact) {
      setError(isMinor
        ? 'Informe celular e email do responsavel para contato.'
        : 'Informe celular e email para contato — do paciente ou do responsavel.');
      return;
    }
    setSaving(true);
    try {
      const payload: PatientIntakeFormData = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v.trim() || null])
      ) as unknown as PatientIntakeFormData;
      payload.verificationBirthDate = verifyDate;
      payload.phone = phoneNational(phones.phone);
      payload.phoneDdi = phones.phone.ddi;
      payload.responsiblePhone1 = phoneNational(phones.responsiblePhone1);
      payload.responsiblePhoneDdi1 = phones.responsiblePhone1.ddi;
      payload.responsiblePhone2 = phoneNational(phones.responsiblePhone2);
      payload.responsiblePhoneDdi2 = phones.responsiblePhone2.ddi;
      await patientsService.submitIntake(alias, token, payload);
      setSubmitted(true);
      window.scrollTo({ top: 0 });
    } catch (err: any) {
      const code = err?.response?.data?.code;
      if (code === 'Patient.IntakeAlreadyCompleted') {
        setState((prev) => prev ? { ...prev, status: 'completed' } : prev);
      } else {
        setError(err?.response?.data?.description || 'Não foi possível enviar. Tente novamente.');
      }
    } finally {
      setSaving(false);
    }
  };

  const clinicName = state?.clinicName || 'Clinic4Us';

  const message = (icon: React.ReactNode, title: string, text: string) => (
    <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
      {icon}
      <Typography variant="h6" sx={{ fontWeight: 600, color: '#495057', mt: 1 }}>{title}</Typography>
      <Typography variant="body2" sx={{ color: '#6c757d', mt: 1 }}>{text}</Typography>
    </Box>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#03B4C6' }} />
        </Box>
      );
    }

    if (submitted) {
      return message(
        <CheckCircle sx={{ fontSize: '3rem', color: '#28a745' }} />,
        'Cadastro enviado!',
        `Obrigado! As informações foram registradas com sucesso na ${clinicName}.`);
    }

    if (state?.status === 'completed') {
      return message(
        <CheckCircle sx={{ fontSize: '3rem', color: '#28a745' }} />,
        'Cadastro já realizado',
        'Este link já foi utilizado e o cadastro foi registrado. Se precisar alterar alguma informação, fale com a clínica.');
    }

    if (state?.status === 'revoked') {
      return message(
        <ErrorOutline sx={{ fontSize: '3rem', color: '#dc3545' }} />,
        'Link bloqueado',
        'Este link foi bloqueado por excesso de tentativas de verificação. Peça um novo link à clínica.');
    }

    if (state?.status === 'expired') {
      return message(
        <EventBusy sx={{ fontSize: '3rem', color: '#ffc107' }} />,
        'Link expirado',
        'Este link era válido por 72 horas. Peça um novo link à clínica para completar o cadastro.');
    }

    if (state?.status !== 'valid') {
      return message(
        <ErrorOutline sx={{ fontSize: '3rem', color: '#dc3545' }} />,
        'Link inválido',
        'Este link de cadastro não é válido. Confira o endereço recebido por email ou peça um novo link à clínica.');
    }

    const row: React.CSSProperties = { display: 'flex', gap: 12, flexWrap: 'wrap' };
    const half: React.CSSProperties = { flex: 1, minWidth: 150 };
    // País + número precisam de um pouco mais que os demais campos antes de quebrar a linha.
    const phoneCell: React.CSSProperties = { ...half, minWidth: 230 };

    if (!verified) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
          <Typography variant="body1" sx={{ color: '#495057', fontWeight: 600 }}>
            Confirmação de segurança
          </Typography>
          <Typography variant="body2" sx={{ color: '#6c757d' }}>
            Para proteger os dados do paciente, confirme a data de nascimento dele para continuar.
          </Typography>
          <TextField
            label="Data de nascimento do paciente"
            type="date"
            required
            value={verifyDate}
            onChange={(e) => { setVerifyDate(e.target.value); setVerifyError(''); }}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            sx={fieldSx}
          />
          {verifyError && (
            <Typography variant="body2" sx={{ color: '#dc3545' }}>{verifyError}</Typography>
          )}
          <Button
            onClick={handleVerify}
            variant="contained"
            disabled={verifying}
            fullWidth
            sx={{
              backgroundColor: '#03B4C6', color: 'white', textTransform: 'none',
              fontWeight: 600, padding: '0.75rem', fontSize: '1rem',
              '&:hover': { backgroundColor: '#029AAB' },
            }}
          >
            {verifying ? 'Verificando...' : 'Continuar'}
          </Button>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" sx={{ color: '#6c757d' }}>
          Confira e complete as informacoes abaixo. Os campos com * sao obrigatorios.
          {isMinor
            ? ' Como o paciente e menor de 18 anos, informe o nome do responsavel e os contatos dele.'
            : ' Celular e email podem ser do paciente ou do responsavel — basta um dos dois.'}
        </Typography>

        <Typography sx={{ fontWeight: 600, color: '#495057', mt: 1 }}>Dados do paciente</Typography>
        <TextField label="Nome completo" required value={form.patientName}
          onChange={(e) => setField('patientName', e.target.value)} size="small" fullWidth
          inputProps={{ maxLength: NAME_MAX }} InputLabelProps={{ shrink: true }} sx={fieldSx} />
        <div style={row}>
          <TextField label="Data de nascimento" type="date" required value={form.birthDate}
            onChange={(e) => setField('birthDate', e.target.value)} size="small" style={half}
            inputProps={{ max: todayIso() }}
            error={isFutureBirthDate(form.birthDate)}
            helperText={isFutureBirthDate(form.birthDate) ? 'A data nao pode ser futura' : ''}
            InputLabelProps={{ shrink: true }} sx={fieldSx} />
          <TextField select label="Sexo biológico" value={form.genderId}
            onChange={(e) => setField('genderId', e.target.value)} size="small" style={half}
            InputLabelProps={{ shrink: true }} sx={fieldSx}>
            <MenuItem value="">Não informar</MenuItem>
            {(state?.genders || []).map((g) => (
              <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
            ))}
          </TextField>
        </div>
        <div style={row}>
          <TextField label="CPF" value={form.document}
            onChange={(e) => setField('document', formatCpf(e.target.value))} size="small" style={half}
            error={!!form.document && !isValidCpf(form.document)}
            helperText={form.document && !isValidCpf(form.document) ? 'CPF invalido' : ' '}
            inputProps={{ maxLength: DOC_MAX }} InputLabelProps={{ shrink: true }} sx={fieldSx} />
          <div style={phoneCell}>
            <PhoneWithCountryInput label="Celular (WhatsApp)" required={!responsibleContactOk} value={phones.phone}
              onChange={setPhone('phone')} countries={state?.phoneCountries ?? []}
              fieldSx={fieldSx} labelProps={{ shrink: true }} />
          </div>
        </div>
        <TextField label="Email" type="email" required={!responsibleContactOk} value={form.email}
          onChange={(e) => setField('email', e.target.value)} size="small" fullWidth
          error={!!form.email.trim() && !isValidEmail(form.email)}
          helperText={form.email.trim() && !isValidEmail(form.email) ? 'Email invalido' : ' '}
          inputProps={{ maxLength: EMAIL_MAX }} InputLabelProps={{ shrink: true }} sx={fieldSx} />

        <Typography sx={{ fontWeight: 600, color: '#495057', mt: 1 }}>
          Responsavel 1{isMinor ? ' (obrigatorio para menor de 18 anos)' : ''}
        </Typography>
        <TextField label="Nome completo" required={isMinor} value={form.responsibleName1}
          onChange={(e) => setField('responsibleName1', e.target.value)} size="small" fullWidth
          inputProps={{ maxLength: NAME_MAX }} InputLabelProps={{ shrink: true }} sx={fieldSx} />
        <div style={row}>
          <TextField label="CPF" value={form.responsibleDocument1}
            onChange={(e) => setField('responsibleDocument1', formatCpf(e.target.value))} size="small" style={half}
            error={!!form.responsibleDocument1 && !isValidCpf(form.responsibleDocument1)}
            helperText={form.responsibleDocument1 && !isValidCpf(form.responsibleDocument1) ? 'CPF invalido' : ' '}
            inputProps={{ maxLength: DOC_MAX }} InputLabelProps={{ shrink: true }} sx={fieldSx} />
          <div style={phoneCell}>
            <PhoneWithCountryInput label="Celular (WhatsApp)" required={isMinor} value={phones.responsiblePhone1}
              onChange={setPhone('responsiblePhone1')} countries={state?.phoneCountries ?? []}
              fieldSx={fieldSx} labelProps={{ shrink: true }} />
          </div>
        </div>
        <TextField label="Email" type="email" required={isMinor} value={form.responsibleEmail1}
          onChange={(e) => setField('responsibleEmail1', e.target.value)} size="small" fullWidth
          error={!!form.responsibleEmail1.trim() && !isValidEmail(form.responsibleEmail1)}
          helperText={form.responsibleEmail1.trim() && !isValidEmail(form.responsibleEmail1) ? 'Email invalido' : ' '}
          inputProps={{ maxLength: EMAIL_MAX }} InputLabelProps={{ shrink: true }} sx={fieldSx} />

        <Typography sx={{ fontWeight: 600, color: '#495057', mt: 1 }}>Responsável 2 (opcional)</Typography>
        <TextField label="Nome completo" value={form.responsibleName2}
          onChange={(e) => setField('responsibleName2', e.target.value)} size="small" fullWidth
          inputProps={{ maxLength: NAME_MAX }} InputLabelProps={{ shrink: true }} sx={fieldSx} />
        <div style={row}>
          <TextField label="CPF" value={form.responsibleDocument2}
            onChange={(e) => setField('responsibleDocument2', formatCpf(e.target.value))} size="small" style={half}
            inputProps={{ maxLength: DOC_MAX }} InputLabelProps={{ shrink: true }} sx={fieldSx} />
          <div style={phoneCell}>
            <PhoneWithCountryInput label="Celular (WhatsApp)" value={phones.responsiblePhone2}
              onChange={setPhone('responsiblePhone2')} countries={state?.phoneCountries ?? []}
              fieldSx={fieldSx} labelProps={{ shrink: true }} />
          </div>
        </div>
        <TextField label="Email" type="email" value={form.responsibleEmail2}
          onChange={(e) => setField('responsibleEmail2', e.target.value)} size="small" fullWidth
          error={!!form.responsibleEmail2.trim() && !isValidEmail(form.responsibleEmail2)}
          helperText={form.responsibleEmail2.trim() && !isValidEmail(form.responsibleEmail2) ? 'Email invalido' : ' '}
          inputProps={{ maxLength: EMAIL_MAX }} InputLabelProps={{ shrink: true }} sx={fieldSx} />

        <Typography sx={{ fontWeight: 600, color: '#495057', mt: 1 }}>Endereço</Typography>
        <div style={row}>
          <TextField label="CEP" value={form.addressZipCode}
            onChange={(e) => setField('addressZipCode', formatCep(e.target.value))}
            onBlur={handleCepBlur} size="small" style={half}
            helperText={cepLoading ? 'Buscando endereco...' : ' '}
            inputProps={{ maxLength: 9 }} InputLabelProps={{ shrink: true }} sx={fieldSx} />
          <TextField label="Número" value={form.addressNumber}
            onChange={(e) => setField('addressNumber', e.target.value)} size="small" style={half}
            inputProps={{ maxLength: 10 }} InputLabelProps={{ shrink: true }} sx={fieldSx} />
        </div>
        <TextField label="Endereço (rua/avenida)" value={form.addressStreet}
          onChange={(e) => setField('addressStreet', e.target.value)} size="small" fullWidth
          inputProps={{ maxLength: 150 }} InputLabelProps={{ shrink: true }} sx={fieldSx} />
        <div style={row}>
          <TextField label="Complemento" value={form.addressComplement}
            onChange={(e) => setField('addressComplement', e.target.value)} size="small" style={half}
            inputProps={{ maxLength: 60 }} InputLabelProps={{ shrink: true }} sx={fieldSx} />
          <TextField label="Bairro" value={form.addressDistrict}
            onChange={(e) => setField('addressDistrict', e.target.value)} size="small" style={half}
            inputProps={{ maxLength: 60 }} InputLabelProps={{ shrink: true }} sx={fieldSx} />
        </div>
        <div style={row}>
          <TextField label="Cidade" value={form.addressCity}
            onChange={(e) => setField('addressCity', e.target.value)} size="small" style={{ flex: 2, minWidth: 150 }}
            inputProps={{ maxLength: 60 }} InputLabelProps={{ shrink: true }} sx={fieldSx} />
          <TextField label="UF" value={form.addressState}
            onChange={(e) => setField('addressState', e.target.value.toUpperCase())} size="small" style={{ flex: 1, minWidth: 80 }}
            inputProps={{ maxLength: 2 }} InputLabelProps={{ shrink: true }} sx={fieldSx} />
        </div>

        <Typography sx={{ fontWeight: 600, color: '#495057', mt: 1 }}>Como conheceu a clínica</Typography>
        <TextField select label="Como conheceu a clínica?" required value={form.registerOrigin}
          onChange={(e) => {
            const value = e.target.value;
            setForm((prev) => ({
              ...prev,
              registerOrigin: value,
              // "Indicado por" só faz sentido quando o canal é uma indicação
              referredBy: value.includes('Indicação') ? prev.referredBy : '',
            }));
            setError('');
          }}
          size="small" fullWidth InputLabelProps={{ shrink: true }} sx={fieldSx}>
          <MenuItem value="">Selecione</MenuItem>
          {ENTRY_CHANNELS.map((c) => (
            <MenuItem key={c} value={c}>{c}</MenuItem>
          ))}
        </TextField>
        <TextField label="Indicado por" value={form.referredBy}
          onChange={(e) => setField('referredBy', e.target.value)}
          disabled={!form.registerOrigin.includes('Indicação')}
          required={form.registerOrigin.includes('Indicação')}
          placeholder="Nome da pessoa ou instituição que indicou"
          size="small" fullWidth inputProps={{ maxLength: 150 }}
          InputLabelProps={{ shrink: true }}
          helperText={form.registerOrigin.includes('Indicação')
            ? 'Quem indicou a clínica'
            : 'Disponível quando o canal for uma indicação'}
          sx={fieldSx} />

        <Typography sx={{ fontWeight: 600, color: '#495057', mt: 1 }}>Observações</Typography>
        <Box sx={{
          padding: '0.75rem 1rem', backgroundColor: '#e8f6f8', color: '#0b6b76',
          borderRadius: '6px', border: '1px solid #b8e6ec',
        }}>
          <Typography variant="body2">
            Use este espaço para informações simples que ajudem no atendimento —
            por exemplo: <em>"o paciente tem preferência por horários pela manhã"</em>.
            <strong> Não inclua informações confidenciais de saúde</strong> (diagnósticos,
            medicamentos, laudos): esses assuntos serão tratados diretamente com a equipe.
          </Typography>
        </Box>
        <TextField label="Observações" value={form.patientNotes} multiline minRows={3}
          onChange={(e) => setField('patientNotes', e.target.value)} size="small" fullWidth
          inputProps={{ maxLength: NOTES_MAX }} InputLabelProps={{ shrink: true }}
          helperText={`${form.patientNotes.length}/${NOTES_MAX}`} sx={fieldSx} />

        {error && (
          <Typography variant="body2" sx={{ color: '#dc3545' }}>{error}</Typography>
        )}

        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          fullWidth
          sx={{
            backgroundColor: '#03B4C6',
            color: 'white',
            textTransform: 'none',
            fontWeight: 600,
            padding: '0.75rem',
            fontSize: '1rem',
            mt: 1,
            '&:hover': { backgroundColor: '#029AAB' },
          }}
        >
          {saving ? 'Enviando...' : 'Enviar cadastro'}
        </Button>
        <Typography variant="caption" sx={{ color: '#adb5bd', textAlign: 'center' }}>
          Após o envio, o link não poderá ser usado novamente.
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f4f6f8', py: { xs: 0, sm: 4 } }}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: 640,
          margin: '0 auto',
          borderRadius: { xs: 0, sm: '12px' },
          overflow: 'hidden',
          border: '1px solid #e9ecef',
        }}
      >
        <Box sx={{ backgroundColor: '#03B4C6', padding: '1.25rem 1.5rem' }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            {clinicName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
            Cadastro de paciente
          </Typography>
        </Box>
        <Box sx={{ padding: { xs: '1rem', sm: '1.5rem' } }}>
          {renderBody()}
        </Box>
      </Paper>
    </Box>
  );
};

export default PatientIntake;
